import React, { useState, useContext, useMemo, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  Box,
  Paper,
  Typography,
  Card,
  TextField,
  Button,
  IconButton,
  Chip,
  InputAdornment,
  Dialog,
  DialogContent,
  Alert,
  CircularProgress,
  styled,
  Avatar,
  Divider,
  LinearProgress,
  Fade,
  // Zoom,
  Slide,
} from '@mui/material';
import {
  Search as SearchIcon,
  // AttachMoney as MoneyIcon,
  // AccountBalance as BalanceIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Scale as ScaleIcon,
  Sort as SortIcon,
  // Timeline as TimelineIcon,
  AccountCircle as AccountCircleIcon,
  // CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptLongIcon,
  Diamond as DiamondIcon,
  // LocalAtm as LocalAtmIcon,
  // Menu as MenuIcon,
  // Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  // TrendingDown as TrendingDownIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  // Star as StarIcon,
  // StarBorder as StarBorderIcon,
  // Info as InfoIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataContext } from '../../context/DataContext';
import PaymentManagementDialog from '../../components/PaymentManagementDialog';
import BillSelectionDialog from '../../components/BillSelectionDialog';

// Add missing import
import PhoneIcon from '@mui/icons-material/Phone';
import PendingIcon from '@mui/icons-material/Pending';

// Professional Styled Components
const GradientBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  // background: theme.palette.mode === 'light'
  //   ? 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)'
  //   : 'linear-gradient(135deg, #0f1424 0%, #1a1f35 100%)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '400px',
    opacity: 0.1,
    zIndex: 0,
  }
}));

const GlassCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.95)'
    : 'rgba(30, 41, 59, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: 20,
  border: `1px solid ${theme.palette.mode === 'light'
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(255, 255, 255, 0.1)'}`,
  boxShadow: theme.palette.mode === 'light'
    ? '0 10px 40px rgba(0, 0, 0, 0.08)'
    : '0 10px 40px rgba(0, 0, 0, 0.2)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 20px 60px rgba(0, 0, 0, 0.12)'
      : '0 20px 60px rgba(0, 0, 0, 0.3)',
  }
}));

// const StatCard = styled(Paper)(({ theme, color }) => ({
//   padding: theme.spacing(3),
//   borderRadius: 20,
//   background: theme.palette.mode === 'light'
//     ? 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
//     : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
//   border: `1px solid ${theme.palette.mode === 'light'
//     ? 'rgba(0, 0, 0, 0.08)'
//     : 'rgba(255, 255, 255, 0.08)'}`,
//   position: 'relative',
//   overflow: 'hidden',
//   '&::after': {
//     content: '""',
//     position: 'absolute',
//     top: 0,
//     right: 0,
//     width: '100px',
//     height: '100px',
//     background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
//     borderRadius: '50%',
//     transform: 'translate(30px, -30px)',
//   }
// }));

const TransactionCard = styled(Paper)(({ theme, pending }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  marginBottom: theme.spacing(2),
  background: theme.palette.mode === 'light'
    ? '#ffffff'
    : '#1e293b',
  border: `1px solid ${theme.palette.mode === 'light'
    ? 'rgba(0, 0, 0, 0.08)'
    : 'rgba(255, 255, 255, 0.08)'}`,
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateX(8px)',
    borderLeft: pending ? `4px solid #f59e0b` : `4px solid #10b981`,
    boxShadow: theme.shadows[4],
  }
}));

const CustomerCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  marginBottom: theme.spacing(1.5),
  background: selected
    ? theme.palette.mode === 'light'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
    : theme.palette.mode === 'light'
      ? '#ffffff'
      : '#1e293b',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: theme.shadows[3],
  }
}));

const CustomerLedger = () => {
  const {
    customers,
    customerTransactions,
    loading,
    error,
    refresh
  } = useContext(DataContext);

  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [transactionDetailOpen, setTransactionDetailOpen] = useState(false);
  const [selectedTransactionDetail, setSelectedTransactionDetail] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedTransactionForPayment, setSelectedTransactionForPayment] = useState(null);
  const [billSelectionOpen, setBillSelectionOpen] = useState(false);
  const [pendingBillsForCustomer, setPendingBillsForCustomer] = useState([]);
  // const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const { updateCustomerTransaction } = useContext(DataContext);

  const getPendingBillsForCustomer = useCallback((customerId) => {
    if (!customerId) return [];
    const customerTxns = customerTransactions.filter(t =>
      t.customer === customerId && t.transactionDetails?.type !== 'cancelled'
    );
    return customerTxns.filter(t => {
      const pendingAmount = (t.summary?.pendingAmount || t.transactionDetails?.balanceDue || 0);
      return pendingAmount > 0 && !t.transactionDetails?.isFullyPaid;
    });
  }, [customerTransactions]);


  const handleCollectPaymentClick = useCallback(() => {
    if (!selectedCustomerId) return;
    const pendingBills = getPendingBillsForCustomer(selectedCustomerId);
    if (pendingBills.length === 0) {
      alert('No pending bills for this customer');
      return;
    }
    if (pendingBills.length === 1) {
      setSelectedTransactionForPayment(pendingBills[0]);
      setPaymentDialogOpen(true);
    } else {
      setPendingBillsForCustomer(pendingBills);
      setBillSelectionOpen(true);
    }
  }, [selectedCustomerId, getPendingBillsForCustomer]);

  const handleBillSelection = useCallback((selectedBill) => {
    setBillSelectionOpen(false);
    setSelectedTransactionForPayment(selectedBill);
    setPaymentDialogOpen(true);
  }, []);

  // In CustomerLedger.jsx, replace the existing recordPayment function with this:

  const recordPayment = async (transactionId, paymentData) => {
    const transaction = customerTransactions.find(t => t.id === transactionId);
    if (!transaction) throw new Error("Transaction not found");

    const formatDate = (date) => {
      if (!date) return "";
      let cleanedDate = typeof date === "string" ? date.replace(/^'/, "") : date;
      if (typeof cleanedDate === "string" && cleanedDate.includes("/")) return cleanedDate;
      const d = new Date(cleanedDate);
      if (isNaN(d.getTime())) return cleanedDate;
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    };

    const formattedPaymentDate = paymentData.date
      ? paymentData.date.replace(/^'/, "")
      : formatDate(new Date().toISOString());

    // Handle MONEY PAYMENT
    if (paymentData.type === 'money') {
      const existingPayments = transaction.paymentDetails?.paymentHistory || [];
      const paymentModes = paymentData.paymentModes;

      const newPaymentEntry = {
        id: `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        date: `'${formattedPaymentDate}`,
        amount: paymentData.amount || 0,
        paymentModes: {
          cash: parseFloat(paymentModes.cash) || 0,
          online: parseFloat(paymentModes.online) || 0,
          cheque: parseFloat(paymentModes.cheque) || 0,
          other: parseFloat(paymentModes.other) || 0,
          advanceBreakup: paymentModes.advanceBreakup || {}
        },
        note: paymentData.note || `Payment on ${formattedPaymentDate}`,
        recordedBy: paymentData.recordedBy || "Cashier"
      };

      const updatedPayments = [...existingPayments, newPaymentEntry];
      const totalPaid = updatedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const existingPaymentDetails = transaction.paymentDetails || {};
      const totalCash = updatedPayments.reduce((sum, p) => sum + (p.paymentModes?.cash || 0), 0);
      const totalOnline = updatedPayments.reduce((sum, p) => sum + (p.paymentModes?.online || 0), 0);
      const totalCheque = updatedPayments.reduce((sum, p) => sum + (p.paymentModes?.cheque || 0), 0);
      const totalOther = updatedPayments.reduce((sum, p) => sum + (p.paymentModes?.other || 0), 0);

      const originalAmount = transaction.transactionDetails?.originalBillAmount ||
        transaction.summary?.grandTotal || 0;

      const newBalance = originalAmount - totalPaid;
      const isMoneyFullyPaid = newBalance <= 0;

      // Check fine status
      const totalFineWeight = transaction.calculatedTotals?.totalFineWeight || 0;
      const totalFineReceived = transaction.paymentDetails?.finePayment?.totalFineReceived || 0;
      const isFineFullyPaid = totalFineWeight > 0 ? (totalFineWeight - totalFineReceived) <= 0 : true;

      const formattedTransactionDate = formatDate(transaction.date);
      const existingDiscount = existingPaymentDetails.discount || transaction.paymentDetails?.discount || 0;
      const existingAdvancePayment = existingPaymentDetails.advancePayment || transaction.paymentDetails?.advancePayment || {
        enabled: false,
        total: 0,
        breakup: {}
      };

      const updatedTransaction = {
        ...transaction,
        date: formattedTransactionDate.startsWith("'") ? formattedTransactionDate : `'${formattedTransactionDate}`,
        paymentDetails: {
          ...existingPaymentDetails,
          paymentHistory: updatedPayments,
          totalPaymentDone: totalPaid,
          cash: totalCash,
          online: totalOnline,
          cheque: totalCheque,
          other: totalOther,
          discount: existingDiscount,
          advancePayment: existingAdvancePayment
        },
        summary: {
          ...transaction.summary,
          paymentDone: totalPaid,
          pendingAmount: newBalance > 0 ? newBalance : 0
        },
        transactionDetails: {
          ...transaction.transactionDetails,
          balanceDue: newBalance > 0 ? newBalance : 0,
          isFullyPaid: isMoneyFullyPaid && isFineFullyPaid,
          paymentStatus: (isMoneyFullyPaid && isFineFullyPaid) ? "paid" : "partial",
          lastPaymentDate: `'${formattedPaymentDate}`,
          lastPaymentAmount: newPaymentEntry.amount
        },
        updated_at: new Date().toISOString()
      };

      const result = await updateCustomerTransaction(transactionId, updatedTransaction);
      if (result.success) {
        refresh();
        return result;
      }
      throw new Error(result.message || "Failed to record payment");
    }

    // Handle FINE PAYMENT
    else if (paymentData.type === 'fine') {
      const existingFinePayment = transaction.paymentDetails?.finePayment || {
        enabled: false,
        totalFineReceived: 0,
        payments: []
      };

      const newFinePayment = {
        id: `fine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        weight: paymentData.weight,
        purity: paymentData.purity,
        actualFine: paymentData.actualFine,
        date: paymentData.date,
        note: paymentData.note || `Fine collection on ${formattedPaymentDate}`,
        recordedBy: paymentData.recordedBy || "Cashier"
      };

      const existingFinePayments = existingFinePayment.payments || [];
      const totalFineReceived = (existingFinePayment.totalFineReceived || 0) + paymentData.actualFine;
      const totalFineWeight = transaction.calculatedTotals?.totalFineWeight || 0;
      const fineBalance = totalFineWeight - totalFineReceived;
      const isFineFullyPaid = fineBalance <= 0;

      // Check money status
      const totalMoneyPaid = transaction.summary?.paymentDone || 0;
      const originalAmount = transaction.transactionDetails?.originalBillAmount ||
        transaction.summary?.grandTotal || 0;
      const isMoneyFullyPaid = (originalAmount - totalMoneyPaid) <= 0;

      const updatedTransaction = {
        ...transaction,
        paymentDetails: {
          ...transaction.paymentDetails,
          finePayment: {
            enabled: true,
            totalFineReceived: totalFineReceived,
            payments: [...existingFinePayments, newFinePayment]
          }
        },
        calculatedTotals: {
          ...transaction.calculatedTotals,
          finePaymentReceived: totalFineReceived,
          fineBalance: fineBalance
        },
        transactionDetails: {
          ...transaction.transactionDetails,
          isFullyPaid: isMoneyFullyPaid && isFineFullyPaid,
          paymentStatus: (isMoneyFullyPaid && isFineFullyPaid) ? "paid" : "partial",
          fineFullyPaid: isFineFullyPaid,
          lastFinePaymentDate: paymentData.date,
          lastFinePaymentAmount: paymentData.actualFine
        },
        updated_at: new Date().toISOString()
      };

      const result = await updateCustomerTransaction(transactionId, updatedTransaction);
      if (result.success) {
        refresh();
        return result;
      }
      throw new Error(result.message || "Failed to record fine payment");
    }
  };

  const handlePaymentComplete = () => {
    refresh();
    setPaymentDialogOpen(false);
    setSelectedTransactionForPayment(null);
  };

  const customersWithBalances = useMemo(() => {
    if (!customers || !customerTransactions) return [];
    return customers.map(customer => {
      const customerTxns = customerTransactions.filter(
        transaction => transaction.customer === customer.id && transaction.transactionDetails?.type !== 'cancelled'
      );

      let totalDue = 0, totalPaid = 0, lastTransactionDate = null, overdueAmount = 0;
      let totalFineWeight = 0, totalFinePaid = 0, totalFineRemaining = 0;

      customerTxns.forEach(transaction => {
        const grandTotal = transaction.summary?.grandTotal || 0;
        const paymentDone = transaction.summary?.paymentDone || 0;
        const pendingAmount = transaction.summary?.pendingAmount || 0;

        // Fine calculations
        const fineWeight = transaction.calculatedTotals?.totalFineWeight || 0;
        const finePaid = transaction.calculatedTotals?.finePaymentReceived ||
          transaction.paymentDetails?.finePayment?.totalFineReceived || 0;
        const fineRemaining = fineWeight - finePaid;

        totalDue += grandTotal;
        totalPaid += paymentDone;
        if (pendingAmount > 0) overdueAmount += pendingAmount;

        totalFineWeight += fineWeight;
        totalFinePaid += finePaid;
        totalFineRemaining += fineRemaining;

        const transactionDate = new Date(transaction.date);
        if (!lastTransactionDate || transactionDate > lastTransactionDate) lastTransactionDate = transactionDate;
      });

      const balance = totalDue - totalPaid;
      const daysOverdue = lastTransactionDate ? Math.floor((new Date() - lastTransactionDate) / (1000 * 60 * 60 * 24)) : 0;

      return {
        ...customer,
        id: customer.id,
        totalTransactions: customerTxns.length,
        totalDue,
        totalPaid,
        balance,
        overdueAmount,
        lastTransactionDate,
        daysOverdue,
        status: balance > 0 ? (balance > 10000 ? 'high' : 'medium') : 'paid',
        // Fine data
        totalFineWeight,
        totalFinePaid,
        totalFineRemaining
      };
    });
  }, [customers, customerTransactions]);

  const getCustomersWithPendingPayments = useCallback(() => {
    if (!customers || !customerTransactions) return [];
    return customers.filter(customer => {
      const customerTrans = customerTransactions.filter(t =>
        t.customer === customer.id && t.transactionDetails?.type !== 'cancelled'
      );
      return customerTrans.some(t => t.summary?.pendingAmount > 0);
    });
  }, [customers, customerTransactions]);

  const customersWithPendingPayments = useMemo(() => {
    const pendingCustomers = getCustomersWithPendingPayments();
    return pendingCustomers.map(customer => {
      const customerData = customersWithBalances.find(c => c.id === customer.id);
      return customerData || customer;
    });
  }, [getCustomersWithPendingPayments, customersWithBalances]);

  const filteredCustomers = useMemo(() => {
    let filtered = customersWithPendingPayments;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name?.toLowerCase().includes(query) ||
        customer.contact?.toString().toLowerCase().includes(query)
      );
    }
    filtered?.sort((a, b) => sortOrder === 'desc' ? b.balance - a.balance : a.balance - b.balance);
    return filtered;
  }, [customersWithPendingPayments, searchQuery, sortOrder]);

  const selectedCustomerDetails = useMemo(() => {
    return customersWithBalances.find(c => c.id === selectedCustomerId);
  }, [selectedCustomerId, customersWithBalances]);

  const selectedCustomerTransactions = useMemo(() => {
    if (!selectedCustomerId) return [];
    const transactions = customerTransactions.filter(t => t.customer === selectedCustomerId);
    return transactions.sort((a, b) => {
      try {
        const dateA = new Date(a.date.split('/').reverse().join('-'));
        const dateB = new Date(b.date.split('/').reverse().join('-'));
        return dateB - dateA;
      } catch {
        return 0;
      }
    });
  }, [selectedCustomerId, customerTransactions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleViewTransactionDetail = useCallback((transactionId) => {
    const transaction = customerTransactions.find(t => t.id === transactionId);
    if (transaction) {
      setSelectedTransactionDetail(transaction);
      setTransactionDetailOpen(true);
    }
  }, [customerTransactions]);

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    // if (isMobile) setMobileDrawerOpen(false);
  };

  // Professional Stats Cards
  // Compact Stats Cards - Space Efficient Version
const renderStatsCards = () => {
  if (!selectedCustomerDetails) return null;

  // Calculate accurate stats
  const totalDue = selectedCustomerDetails.totalDue || 0;
  const totalPaid = selectedCustomerDetails.totalPaid || 0;
  const pendingBalance = totalDue - totalPaid;
  
  const totalFineWeight = selectedCustomerDetails.totalFineWeight || 0;
  const fineCollected = selectedCustomerDetails.totalFinePaid || 0;
  const finePending = totalFineWeight - fineCollected;
  
  // const moneyProgress = totalDue > 0 ? (totalPaid / totalDue) * 100 : 0;
  // const fineProgress = totalFineWeight > 0 ? (fineCollected / totalFineWeight) * 100 : 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Single Row Grid - All stats in one line */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(4, 1fr)',
          md: totalFineWeight > 0 ? 'repeat(6, 1fr)' : 'repeat(4, 1fr)'
        },
        gap: 1.5,
        mb: 2
      }}>
        {/* Total Due */}
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Total Due</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(totalDue)}
          </Typography>
        </Paper>

        {/* Total Paid */}
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PaymentIcon sx={{ fontSize: 16, color: '#10b981' }} />
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Total Paid</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(totalPaid)}
          </Typography>
        </Paper>

        {/* Pending Balance */}
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: pendingBalance > 0 ? '#fef2f2' : '#f0fdf4', border: `1px solid ${pendingBalance > 0 ? '#fecaca' : '#bbf7d0'}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <WarningIcon sx={{ fontSize: 16, color: pendingBalance > 0 ? '#ef4444' : '#10b981' }} />
            <Typography variant="caption" sx={{ color: pendingBalance > 0 ? '#ef4444' : '#10b981', fontWeight: 500 }}>Pending</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: pendingBalance > 0 ? '#dc2626' : '#059669' }}>
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(pendingBalance)}
          </Typography>
        </Paper>

        

        {/* Fine Stats - Only if fine exists */}
        {totalFineWeight > 0 && (
          <>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ScaleIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Total Fine</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {totalFineWeight.toFixed(1)} gm
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Collected</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                {fineCollected.toFixed(1)} gm
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, bgcolor: finePending > 0 ? '#fef2f2' : '#f0fdf4', border: `1px solid ${finePending > 0 ? '#fecaca' : '#bbf7d0'}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ScaleIcon sx={{ fontSize: 16, color: finePending > 0 ? '#f59e0b' : '#10b981' }} />
                <Typography variant="caption" sx={{ color: finePending > 0 ? '#f59e0b' : '#10b981', fontWeight: 500 }}>Fine Pending</Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: finePending > 0 ? '#d97706' : '#059669' }}>
                {finePending.toFixed(1)} gm
              </Typography>
            </Paper>
          </>
        )}
      </Box>

      
    </Box>
  );
};

  // Professional Transaction List
  const renderTransactionList = () => {
    if (!selectedCustomerId) {
      return (
        <GlassCard sx={{ p: 6, textAlign: 'center' }}>
          <ReceiptLongIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
            No Customer Selected
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Please select a customer from the list to view their transactions
          </Typography>
        </GlassCard>
      );
    }

    const activeTransactions = selectedCustomerTransactions.filter(
      t => t.transactionDetails?.type !== 'cancelled'
    );

    if (activeTransactions.length === 0) {
      return (
        <GlassCard sx={{ p: 6, textAlign: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            No Transactions Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This customer has no transaction history
          </Typography>
        </GlassCard>
      );
    }

    return activeTransactions.map((transaction, index) => {
      const grandTotal = transaction.summary?.grandTotal || 0;
      const paymentDone = transaction.summary?.paymentDone || 0;
      const pendingAmount = transaction.summary?.pendingAmount || 0;

      // Fine data
      const totalFineWeight = transaction.calculatedTotals?.totalFineWeight || 0;
      const finePaid = transaction.calculatedTotals?.finePaymentReceived ||
        transaction.paymentDetails?.finePayment?.totalFineReceived || 0;
      const fineRemaining = totalFineWeight - finePaid;

      const billNo = transaction.transactionDetails?.billNo || 'N/A';
      const date = transaction.date || 'Unknown Date';
      const itemsCount = transaction.items?.length || 0;
      const isMoneyPending = pendingAmount > 0;
      const isFinePending = fineRemaining > 0;
      const isPending = isMoneyPending || isFinePending;

      const paidPercentage = grandTotal > 0 ? (paymentDone / grandTotal) * 100 : 0;
      const finePaidPercentage = totalFineWeight > 0 ? (finePaid / totalFineWeight) * 100 : 0;

      return (
        <Slide direction="up" in key={transaction.id} timeout={300 + index * 50}>
          <TransactionCard pending={isPending} onClick={() => handleViewTransactionDetail(transaction.id)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Chip
                    size="small"
                    icon={isPending ? <PendingIcon /> : <CheckCircleIcon />}
                    label={isPending ? 'Pending Payment' : 'Fully Paid'}
                    sx={{
                      bgcolor: isPending ? '#fef3c7' : '#d1fae5',
                      color: isPending ? '#d97706' : '#059669',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${itemsCount} Item${itemsCount !== 1 ? 's' : ''}`}
                    sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 500 }}
                  />
                  {totalFineWeight > 0 && (
                    <Chip
                      size="small"
                      icon={<ScaleIcon />}
                      label={`Fine: ${fineRemaining.toFixed(3)} gm pending`}
                      sx={{ bgcolor: '#fef3c7', color: '#d97706', fontWeight: 500 }}
                    />
                  )}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Bill #{billNo}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {date.toString()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                  {formatCurrency(grandTotal)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Total Amount
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Money Payment Progress */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Money Payment Progress</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                  {paidPercentage.toFixed(1)}% Paid
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={paidPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: isMoneyPending ? '#f59e0b' : '#10b981',
                    borderRadius: 4,
                  }
                }}
              />
            </Box>

            {/* Fine Payment Progress (if applicable) */}
            {totalFineWeight > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScaleIcon sx={{ fontSize: 14 }} /> Fine Collection Progress
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                    {finePaidPercentage.toFixed(1)}% Collected
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={finePaidPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#e2e8f0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: isFinePending ? '#f59e0b' : '#10b981',
                      borderRadius: 4,
                    }
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Paid Amount
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                    {formatCurrency(paymentDone)}
                  </Typography>
                </Box>
                {isMoneyPending && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      Due Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                      {formatCurrency(pendingAmount)}
                    </Typography>
                  </Box>
                )}
                {totalFineWeight > 0 && (
                  <>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        Fine Paid
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                        {finePaid.toFixed(3)} gm
                      </Typography>
                    </Box>
                    {isFinePending && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          Fine Remaining
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#f59e0b' }}>
                          {fineRemaining.toFixed(3)} gm
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {isPending && (
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTransactionForPayment(transaction);
                    setPaymentDialogOpen(true);
                  }}
                  sx={{
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    }
                  }}
                >
                  Collect Payment / Fine
                </Button>
              )}
            </Box>
          </TransactionCard>
        </Slide>
      );
    });
  };

  // Professional Customer List
  const renderCustomerList = () => {
    if (filteredCustomers.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PeopleIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            No customers found
          </Typography>
        </Box>
      );
    }

    return filteredCustomers.map((customer, index) => {
      const pendingAmount = customer.balance + (customer.openingBalance || 0);

      return (
        <Fade in key={customer.id} timeout={300 + index * 50}>
          <CustomerCard selected={selectedCustomerId === customer.id} onClick={() => handleSelectCustomer(customer.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  background: selectedCustomerId === customer.id
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontSize: 24,
                  fontWeight: 600,
                }}
              >
                {customer.name?.charAt(0) || 'C'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: selectedCustomerId === customer.id ? 'white' : 'text.primary' }}>
                    {customer.name || 'Unknown Customer'}
                  </Typography>
                  <Chip
                    label={formatCurrency(pendingAmount)}
                    size="small"
                    sx={{
                      bgcolor: selectedCustomerId === customer.id
                        ? 'rgba(255, 255, 255, 0.2)'
                        : '#fee2e2',
                      color: selectedCustomerId === customer.id ? 'white' : '#dc2626',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: selectedCustomerId === customer.id ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                    {customer.contact || 'No contact'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: selectedCustomerId === customer.id ? 'rgba(255,255,255,0.6)' : '#94a3b8' }}>
                    {customer.totalTransactions || 0} transactions
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CustomerCard>
        </Fade>
      );
    });
  };

  const renderTransactionDetail = () => {
    if (!selectedTransactionDetail) return null;

    const transaction = selectedTransactionDetail;
    const customer = customers.find(c => c.id === transaction.customer) || {};
    const items = transaction.items || [];
    const calculatedTotals = transaction.calculatedTotals || {};
    const paymentDetails = transaction.paymentDetails || {};
    const summary = transaction.summary || {};
    const transactionDetails = transaction.transactionDetails || {};
    const isPending = summary.pendingAmount > 0;

    const totalFineWeight = calculatedTotals.totalFineWeight || 0;
    const finePaymentReceived = calculatedTotals.finePaymentReceived ||
      paymentDetails?.finePayment?.totalFineReceived || 0;
    const fineBalance = calculatedTotals.fineBalance || (totalFineWeight - finePaymentReceived);

    return (
      <GlassCard sx={{ p: { xs: 2, sm: 4 }, maxHeight: '85vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setTransactionDetailOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Back to Ledger
          </Button>
          <Chip
            label={isPending ? 'Payment Pending' : 'Fully Paid'}
            sx={{
              bgcolor: isPending ? '#fef3c7' : '#d1fae5',
              color: isPending ? '#d97706' : '#059669',
              fontWeight: 600,
              fontSize: '0.8rem',
              py: 2,
            }}
          />
        </Box>

        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            Invoice #{transactionDetails.billNo || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Generated on {transaction.date || 'Unknown Date'}
          </Typography>
        </Box>

        {/* Customer Info */}
        <GlassCard sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountCircleIcon /> Customer Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Full Name</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{customer.name || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Contact Number</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{customer.contact || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Email Address</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{customer.emailId || 'N/A'}</Typography>
            </Box>
          </Box>
        </GlassCard>

        {/* Items Table */}
        <GlassCard sx={{ p: 3, mb: 3, overflowX: 'auto' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DiamondIcon /> Items Purchased ({items.length})
          </Typography>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: 2, borderColor: 'divider' }}>
                <Box component="th" sx={{ p: 1, textAlign: 'left' }}>#</Box>
                <Box component="th" sx={{ p: 1, textAlign: 'left' }}>Item</Box>
                <Box component="th" sx={{ p: 1, textAlign: 'left' }}>HUID</Box>
                <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Weight</Box>
                <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Rate</Box>
                <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Total</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {items.map((item, idx) => (
                <Box component="tr" key={idx} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Box component="td" sx={{ p: 1 }}>{idx + 1}</Box>
                  <Box component="td" sx={{ p: 1 }}>{item.name}</Box>
                  <Box component="td" sx={{ p: 1 }}>{item.huid || '-'}</Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'right' }}>{item.weight}g</Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'right' }}>₹{item.metalRate}</Box>
                  <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </GlassCard>

        {/* Summary Section */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          <GlassCard sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Bill Summary</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatCurrency(calculatedTotals.subtotal || 0)}</Typography>
            </Box>
            {summary?.cgst !== 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">CGST (1.5%)</Typography>
                  <Typography variant="body2">{formatCurrency(summary.cgst || 0)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">SGST (1.5%)</Typography>
                  <Typography variant="body2">{formatCurrency(summary.sgst || 0)}</Typography>
                </Box>
              </>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Grand Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#3b82f6' }}>{formatCurrency(summary.grandTotal || 0)}</Typography>
            </Box>
          </GlassCard>

          <GlassCard sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Payment Details</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Total Paid</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(summary.paymentDone || 0)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Pending Amount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: summary.pendingAmount > 0 ? '#ef4444' : '#10b981' }}>
                {formatCurrency(summary.pendingAmount || 0)}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {paymentDetails.cash > 0 && <Chip label={`Cash: ${formatCurrency(paymentDetails.cash)}`} size="small" />}
              {paymentDetails.online > 0 && <Chip label={`Online: ${formatCurrency(paymentDetails.online)}`} size="small" />}
              {paymentDetails.cheque > 0 && <Chip label={`Cheque: ${formatCurrency(paymentDetails.cheque)}`} size="small" />}
            </Box>
            {totalFineWeight > 0 && (
              <GlassCard sx={{ p: 3, mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScaleIcon /> Fine Details
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Total Fine Weight</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{totalFineWeight.toFixed(3)} gm</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Fine Paid</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                    {finePaymentReceived.toFixed(3)} gm
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Fine Remaining</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: fineBalance > 0 ? '#f59e0b' : '#10b981' }}>
                    {fineBalance.toFixed(3)} gm
                  </Typography>
                </Box>
              </GlassCard>
            )}
          </GlassCard>
        </Box>

        {/* Action Buttons */}
        {isPending && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => {
                setTransactionDetailOpen(false);
                setSelectedTransactionForPayment(transaction);
                setPaymentDialogOpen(true);
              }}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                px: 4,
                py: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                }
              }}
            >
              Record Payment
            </Button>
          </Box>
        )}
      </GlassCard>
    );
  };

  if (loading) {
    return (
      <GradientBackground sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#667eea', mb: 3 }} />
          <Typography variant="h6">Loading customer data...</Typography>
        </Box>
      </GradientBackground>
    );
  }

  if (error) {
    return (
      <GradientBackground sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <GlassCard sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading data: {error}
          </Alert>
          <Button variant="contained" onClick={refresh} startIcon={<RefreshIcon />}>
            Retry
          </Button>
        </GlassCard>
      </GradientBackground>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <GradientBackground>
        <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, sm: 3, md: 4 } }}>

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: '#1e293b' }}>
              Customer Ledger
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Track and manage customer payments and transaction history
            </Typography>
          </Box>

          {/* Main Layout */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>

            {/* Customer List Sidebar */}
            <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
              <GlassCard sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Customers <Chip label={filteredCustomers.length} size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <IconButton size="small" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                    <SortIcon />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', pr: 1 }}>
                  {renderCustomerList()}
                </Box>
              </GlassCard>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1 }}>
              {selectedCustomerDetails ? (
                <>
                  {/* Customer Header */}
                  <GlassCard sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 64, height: 64, bgcolor: '#667eea' }}>
                          {selectedCustomerDetails.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {selectedCustomerDetails.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14 }} /> {selectedCustomerDetails.contact || 'N/A'}
                            </Typography>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ReceiptIcon sx={{ fontSize: 14 }} /> {selectedCustomerDetails.totalTransactions || 0} transactions
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={handleCollectPaymentClick}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          px: 4,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          }
                        }}
                      >
                        Collect Payment / Fine
                      </Button>
                    </Box>
                  </GlassCard>

                  {/* Stats Cards */}
                  {renderStatsCards()}

                  {/* Transactions Section */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLongIcon sx={{ color: '#667eea' }} />
                    Transaction History ({selectedCustomerTransactions.filter(t => t.transactionDetails?.type !== 'cancelled').length})
                  </Typography>

                  {renderTransactionList()}
                </>
              ) : (
                <GlassCard sx={{ p: 8, textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h5" sx={{ color: '#64748b', mb: 1 }}>
                    Select a Customer
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Choose a customer from the left panel to view their ledger details
                  </Typography>
                </GlassCard>
              )}
            </Box>
          </Box>
        </Box>

        <Dialog
          open={transactionDetailOpen}
          onClose={() => setTransactionDetailOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', m: 2 } }}
        >
          <DialogContent sx={{ p: 0 }}>
            {renderTransactionDetail()}
          </DialogContent>
        </Dialog>

        <PaymentManagementDialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setSelectedTransactionForPayment(null);
          }}
          transaction={selectedTransactionForPayment}
          customer={selectedCustomerDetails}
          onPaymentComplete={handlePaymentComplete}
          recordPayment={recordPayment}
        />

        <BillSelectionDialog
          open={billSelectionOpen}
          onClose={() => {
            setBillSelectionOpen(false);
            setPendingBillsForCustomer([]);
          }}
          pendingTransactions={pendingBillsForCustomer}
          onSelectBill={handleBillSelection}
          formatCurrency={formatCurrency}
        />
      </GradientBackground>
    </LocalizationProvider>
  );
};


export default CustomerLedger;